import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  Tabs,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { fetchPartnerApplications, fetchPartners } from '../../api/partners';

const PartnersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [partners, setPartners] = useState([]);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [partnersResponse, applicationsResponse] = await Promise.all([
          fetchPartners(),
          fetchPartnerApplications(),
        ]);

        if (!isMounted) return;

        setPartners(Array.isArray(partnersResponse) ? partnersResponse : []);
        setApplications(Array.isArray(applicationsResponse) ? applicationsResponse : []);
      } catch (err) {
        console.error('Failed to load partner data:', err);
        if (isMounted) {
          setError('Unable to load partner data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'campus_micro_factory':
        return 'info';
      case 'micro_factory':
        return 'success';
      case 'regional_hub':
        return 'primary';
      case 'specialized_center':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'error';
      case 'pending_review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'info';
      default:
        return 'default';
    }
  };

  const totalPartners = partners.filter(
    (partner) => partner.status !== 'rejected' && partner.status !== 'offboarded'
  ).length;
  const activePartners = partners.filter((partner) => partner.status === 'active');

  const totalCapacity = activePartners.reduce((sum, partner) => {
    const capacity =
      partner.productionCapacity?.monthlyCapacity ??
      partner.productionCapacity?.maxUnits ??
      partner.productionCapacity?.minUnits ??
      0;
    return sum + (capacity || 0);
  }, 0);

  const formatCapacity = (partner) => {
    const capacity =
      partner.productionCapacity?.monthlyCapacity ??
      partner.productionCapacity?.maxUnits ??
      partner.productionCapacity?.minUnits;
    return capacity ? `${capacity.toLocaleString()} units/mo` : '—';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Manufacturing Partner Network
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage manufacturing partners and their applications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/partners/new')}
        >
          Add Partner
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Partners
                  </Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {totalPartners}
                  </Typography>
                </Box>
                <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Active Partners
                  </Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {activePartners.length}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Pending Applications
                  </Typography>
                  <Typography variant="h4" fontWeight={600}>
                    {applications.filter(
                      (a) => a.status === 'pending_review' || a.status === 'draft'
                    ).length}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Production Capacity
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
                  >
                    {totalCapacity.toLocaleString()}
                    <Typography component="span" variant="caption" color="text.secondary">
                      units/month
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Active Partners" />
          <Tab label="Applications" />
        </Tabs>

        {/* Active Partners Tab */}
        {tabValue === 0 && (
          <CardContent>
            {activePartners.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Active Partners Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Get started by adding your first manufacturing partner or activating a pending partner
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/partners/new')}
                >
                  Add Partner
                </Button>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead sx={{ '& .MuiTableCell-root': { color: 'common.white' } }}>
                    <TableRow>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell>Capabilities</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activePartners.map((partner) => (
                      <TableRow key={partner.id}>
                        <TableCell>{partner.legalName || partner.dbaName || '—'}</TableCell>
                        <TableCell>
                          <Chip label={partner.tier} color={getTierColor(partner.tier)} size="small" />
                        </TableCell>
                        <TableCell>{partner.capabilities?.length || 0}</TableCell>
                        <TableCell>{formatCapacity(partner)}</TableCell>
                        <TableCell>
                          <Chip label={partner.status} color={getStatusColor(partner.status)} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => navigate(`/partners/${partner.id}`)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        )}

        {/* Applications Tab */}
        {tabValue === 1 && (
          <CardContent>
            {applications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <PendingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Pending Applications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All applications have been reviewed
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead sx={{ '& .MuiTableCell-root': { color: 'common.white' } }}>
                    <TableRow>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Desired Tier</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell>{app.companyName}</TableCell>
                        <TableCell>
                          <Chip label={app.desiredTier} color={getTierColor(app.desiredTier)} size="small" />
                        </TableCell>
                        <TableCell>
                          {app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={app.status} color={getStatusColor(app.status)} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => navigate(`/partners/applications/${app.id}`)}>
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        )}
      </Card>
    </Box>
  );
};

export default PartnersPage;
